import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import { Building2, Users, CheckCircle2, UserCheck, Briefcase, GraduationCap, Gauge } from "lucide-react";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5">
      <span className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: accent + "1A" }}><Icon className="h-5 w-5" style={{ color: accent }} /></span>
      <div className="num font-bold text-3xl text-ink mt-3">{value}</div>
      <div className="text-xs font-semibold text-ink-faint mt-0.5">{label}</div>
    </Card>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-ink-soft">Loading…</div>;
  const k = d.kpis;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Platform Administrator</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Platform Overview</h1>
        <p className="text-ink-soft text-sm mt-1">Manage colleges, candidates, experts and platform content across all tenants.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Building2} label="Colleges" value={k.colleges} accent="#0055FF" />
        <Kpi icon={Users} label="Candidates" value={k.candidates} accent="#10B981" />
        <Kpi icon={CheckCircle2} label="Completed Interviews" value={k.completed_interviews} accent="#F59E0B" />
        <Kpi icon={UserCheck} label="Interview Experts" value={k.experts} accent="#8B5CF6" />
        <Kpi icon={Briefcase} label="Job Roles" value={k.job_roles} accent="#06B6D4" />
        <Kpi icon={GraduationCap} label="Courses" value={k.courses} accent="#EC4899" />
        <Kpi icon={Gauge} label="Avg Platform Score" value={k.avg_score} accent="#0055FF" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4">Colleges (Tenants)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200">
                <th className="px-3 py-2.5">College</th><th className="px-3 py-2.5">Code</th><th className="px-3 py-2.5">Credits</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {d.colleges.map((c) => (
                  <tr key={c.id} data-testid={`college-row-${c.code}`}>
                    <td className="px-3 py-3 font-semibold text-ink">{c.name}<div className="text-xs text-ink-faint font-normal">{c.location}</div></td>
                    <td className="px-3 py-3 num text-ink-soft">{c.code}</td>
                    <td className="px-3 py-3 num text-ink-soft">{c.credits_used}/{c.credits_purchased}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4">Interviews by Job Role</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart layout="vertical" data={d.role_distribution} margin={{ top: 0, right: 16, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: "#475569", fontSize: 10 }} axisLine={false} tickLine={false} width={120} />
              <Tooltip />
              <Bar dataKey="value" fill="#0055FF" radius={[0, 6, 6, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-3">Platform Content</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Job Roles", value: `${k.job_roles} configured` },
            { label: "Interview Questions", value: "Role-based templates" },
            { label: "Coding Challenges", value: "10 challenges" },
            { label: "Courses", value: `${k.courses} in catalog` },
          ].map((x) => (
            <div key={x.label} className="rounded-xl border border-slate-200 p-4">
              <div className="text-sm font-semibold text-ink">{x.label}</div>
              <div className="text-xs text-ink-faint mt-0.5">{x.value}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
